import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import CamposTramiteExpres from './CamposTramiteExpres';
import { registrarTramiteExpres } from '../../services/turneroService';
import { buscarClientes } from '../../services/clienteService';
import { DATOS_EXPRES_VACIOS, datosExpresValidos } from '../../constants/tramiteExpres';
import { apellidoNombre, textoPersona } from '../../utils/nombrePersona';

const CLIENTE_VACIO = { dni: '', cuil: '', apellido: '', nombre: '', telefono: '' };

/**
 * Trámite exprés de alguien que no tuvo turno hoy: pasa por el mostrador solo a entregar o retirar
 * documentación y se va (POST /recepcion/turnos/expres). Nace FINALIZADO y no cuenta en las
 * estadísticas. El exprés de alguien ya atendido hoy va por ModalReingresoCliente.
 *
 * El buscador de clientes solo completa los datos: el vínculo con el cliente lo resuelve el
 * backend, igual que en el alta de un turno.
 */
// El DNI no viene en el cliente: sale del CUIL (20-DNI-X), que es como lo guarda el padrón.
function dniDeCuil(cuil) {
    const digitos = String(cuil || '').replace(/\D/g, '');
    return digitos.length === 11 ? String(Number(digitos.substring(2, 10))) : '';
}

function datosDeCliente(c) {
    if (!c) return CLIENTE_VACIO;
    return {
        nombre: c.nombre || '',
        apellido: c.apellido || '',
        cuil: c.cuil || '',
        dni: c.dni || dniDeCuil(c.cuil),
        telefono: c.telefonoPropio || '',
    };
}

/**
 * `clienteInicial`: cliente del padrón ya elegido en el buscador de "Llegó cliente"; precarga los
 * datos y deja el buscador de acá solo para corregirlo.
 */
function ModalTramiteExpres({ show, onCerrar, onRegistrado, clienteInicial = null }) {
    const [cliente, setCliente] = useState(CLIENTE_VACIO);
    const [datosExpres, setDatosExpres] = useState(DATOS_EXPRES_VACIOS);
    const [busqueda, setBusqueda] = useState('');
    const [encontrados, setEncontrados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [procesando, setProcesando] = useState(false);
    const [error, setError] = useState('');
    const debounceRef = useRef(null);

    // Se resetea al abrir: depende del CUIL y no del objeto, que el padre puede recrear.
    useEffect(() => {
        if (!show) return;
        setCliente(datosDeCliente(clienteInicial));
        setDatosExpres(DATOS_EXPRES_VACIOS);
        setBusqueda('');
        setEncontrados([]);
        setError('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, clienteInicial?.cuil]);

    // Mismo debounce de 350ms que el buscador de clientes de ModalCrearTurno.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!busqueda.trim()) {
            setEncontrados([]);
            setBuscando(false);
            return undefined;
        }
        setBuscando(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const resultado = await buscarClientes(busqueda.trim());
                setEncontrados(Array.isArray(resultado) ? resultado.slice(0, 6) : []);
            } catch (e) {
                setEncontrados([]);
            } finally {
                setBuscando(false);
            }
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [busqueda]);

    const elegirCliente = (c) => {
        const datos = datosDeCliente(c);
        setCliente((prev) => ({
            ...datos,
            dni: datos.dni || prev.dni,
            telefono: datos.telefono || prev.telefono,
        }));
        setBusqueda('');
        setEncontrados([]);
    };

    const cambiarCliente = (campo) => (e) => setCliente((prev) => ({ ...prev, [campo]: e.target.value }));

    const puedeConfirmar = !procesando
        && !!cliente.apellido.trim() && !!cliente.nombre.trim()
        && datosExpresValidos(datosExpres);

    const cerrar = useCallback(() => {
        if (procesando) return;
        onCerrar?.();
    }, [procesando, onCerrar]);

    const confirmar = async () => {
        if (!puedeConfirmar) return;
        setProcesando(true);
        setError('');
        try {
            const creado = await registrarTramiteExpres({
                nombre: textoPersona(cliente.nombre),
                apellido: textoPersona(cliente.apellido),
                dni: cliente.dni.trim() || null,
                cuil: cliente.cuil.trim() || null,
                telefonoCliente: cliente.telefono.trim() || null,
                documentacion: datosExpres.documentacion.trim(),
                motivoExpres: datosExpres.motivo,
                motivoExpresDetalle: datosExpres.motivoDetalle.trim() || null,
                observaciones: datosExpres.observaciones.trim() || null,
            });
            onRegistrado?.(creado);
        } catch (e) {
            setError(e?.response?.data?.mensaje || 'No se pudo registrar el trámite exprés.');
        } finally {
            setProcesando(false);
        }
    };

    return (
        <Modal className="rec-modal" show={show} onHide={cerrar} centered scrollable size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-lightning-charge-fill me-2"></i>
                    Trámite exprés
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="rec-modal-resumen rounded-4 small">
                    <i className="bi bi-info-circle me-1"></i>
                    El cliente no tuvo turno hoy: solo entrega o retira documentación. Queda registrado y
                    finalizado en el acto, y no cuenta en las estadísticas.
                </div>

                <label className="form-label fw-semibold mt-3 mb-2" htmlFor="expres-buscar-cliente">
                    Buscar cliente <span className="text-secondary fw-normal">(opcional)</span>
                </label>
                <div className="position-relative">
                    <input
                        id="expres-buscar-cliente"
                        type="text"
                        className="form-control rounded-4"
                        placeholder="Nombre, DNI o CUIL…"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        disabled={procesando}
                        autoComplete="off"
                    />
                    {(buscando || encontrados.length > 0) && (
                        <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 5, top: '100%' }}>
                            {buscando ? (
                                <div className="list-group-item small text-secondary">
                                    <span className="spinner-border spinner-border-sm me-2" />Buscando…
                                </div>
                            ) : encontrados.map((c) => (
                                <button
                                    key={c.cuil || `${c.apellido}-${c.nombre}`}
                                    type="button"
                                    className="list-group-item list-group-item-action"
                                    onClick={() => elegirCliente(c)}
                                >
                                    <div className="fw-semibold">{apellidoNombre(c.apellido, c.nombre)}</div>
                                    <div className="small text-secondary">CUIL {c.cuil || '-'}</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="row g-2 mt-1">
                    <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" htmlFor="expres-apellido">Apellido *</label>
                        <input id="expres-apellido" className="form-control rounded-4" value={cliente.apellido}
                               onChange={cambiarCliente('apellido')} disabled={procesando} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" htmlFor="expres-nombre">Nombre *</label>
                        <input id="expres-nombre" className="form-control rounded-4" value={cliente.nombre}
                               onChange={cambiarCliente('nombre')} disabled={procesando} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label fw-semibold mb-1" htmlFor="expres-dni">DNI</label>
                        <input id="expres-dni" className="form-control rounded-4" inputMode="numeric" value={cliente.dni}
                               onChange={cambiarCliente('dni')} disabled={procesando} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label fw-semibold mb-1" htmlFor="expres-cuil">CUIL</label>
                        <input id="expres-cuil" className="form-control rounded-4" value={cliente.cuil}
                               onChange={cambiarCliente('cuil')} disabled={procesando} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label fw-semibold mb-1" htmlFor="expres-telefono">Teléfono</label>
                        <input id="expres-telefono" className="form-control rounded-4" inputMode="tel" value={cliente.telefono}
                               onChange={cambiarCliente('telefono')} disabled={procesando} />
                    </div>
                </div>

                <CamposTramiteExpres
                    valor={datosExpres}
                    onChange={setDatosExpres}
                    disabled={procesando}
                    idPrefijo="expres-sin-turno"
                />

                {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <button className="rec-btn-secondary" onClick={cerrar} disabled={procesando}>Cancelar</button>
                <button className="rec-btn-primary" onClick={confirmar} disabled={!puedeConfirmar}>
                    {procesando
                        ? <span className="spinner-border spinner-border-sm me-1" />
                        : <i className="bi bi-check2-all me-1" />}
                    Registrar y finalizar
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalTramiteExpres;
